import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../Revenue/revenue.css";
import { walletAdjust, walletOwner, walletSearch, walletTransactions, errorMessage } from "../Revenue/revenueApi";
import { Avatar, Loading, Pager, UserCell } from "../Revenue/parts";
import { REASON_LABELS, dateTime, fmt } from "../Revenue/format";

/// Admin balances: pick a user or a target category, see what it holds, add
/// or subtract coins (users: also diamonds) with a note, and read its full
/// history. Paying a category out = subtract its coins here with a note.
export default function Wallet() {
  const [params, setParams] = useSearchParams();
  const [type, setType] = useState(params.get("type") === "category" ? "category" : "user");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(
    params.get("id") ? { type: params.get("type") === "category" ? "category" : "user", id: Number(params.get("id")) } : null
  );

  // Search as you type (categories list without a query).
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      if (type === "user" && !q.trim()) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const r = await walletSearch(type, q.trim());
        if (alive) setResults(r.data || []);
      } catch {
        if (alive) setResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [type, q]);

  const choose = (row) => {
    setSelected({ type: row.type, id: row.id });
    setParams({ type: row.type, id: String(row.id) });
  };

  return (
    <div className="rv-wrap">
      <h2 className="rv-title mb-1">Wallet</h2>
      <p className="rv-sub mb-4">Add or subtract coins and diamonds. Every change is saved in the owner's history.</p>

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="rv-panel h-100">
            <div className="rv-seg mb-3">
              {[
                ["user", "Users"],
                ["category", "Categories"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  className={type === k ? "active" : ""}
                  onClick={() => {
                    setType(k);
                    setQ("");
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
            <input
              className="rv-input mb-3"
              placeholder={type === "user" ? "Search name or id…" : "Filter categories…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {searching && <Loading />}
            {!searching && results.length === 0 && (
              <div className="rv-empty">{type === "user" && !q.trim() ? "Type a name or an id." : "Nothing found."}</div>
            )}
            {results.map((r) => (
              <div
                key={`${r.type}-${r.id}`}
                className="rv-stat-row"
                style={{
                  cursor: "pointer",
                  borderRadius: 10,
                  padding: "0.55rem",
                  background: selected?.type === r.type && selected?.id === r.id ? "rgba(144,87,229,0.15)" : "transparent",
                }}
                onClick={() => choose(r)}
              >
                <UserCell user={r} sub={r.type === "user" ? `#${r.id} · ${r.nick_name || ""}` : `#${r.id}`} />
                <span className="text-end small">
                  <span className="d-block" style={{ color: "#f4d134" }}>{fmt(r.coins)} c</span>
                  {r.type === "user" && <span className="d-block text-grey">{fmt(r.diamonds)} d</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="col-lg-8">
          {selected ? (
            <OwnerPanel key={`${selected.type}-${selected.id}`} type={selected.type} id={selected.id} />
          ) : (
            <div className="rv-panel h-100 d-flex align-items-center justify-content-center">
              <div className="rv-empty">Pick a user or a category.</div>
            </div>
          )}
        </div>
      </div>

      <AllMovements />
    </div>
  );
}

function OwnerPanel({ type, id }) {
  const [owner, setOwner] = useState(null);
  const [txs, setTxs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currency, setCurrency] = useState("coins");
  const [direction, setDirection] = useState("add");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await walletOwner(type, id, { page, limit: 15 });
      setOwner(r.owner);
      setTxs(r.transactions || []);
      setMeta(r.meta);
    } catch (e) {
      setError(errorMessage(e, "Could not load"));
    } finally {
      setLoading(false);
    }
  }, [type, id, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Decimals allowed (up to 4 — what the database keeps); shown with ≤ 2.
  const amt = parseFloat(amount);
  const decimalsOk = /^\d+(\.\d{1,4})?$/.test(String(amount).trim());
  const current = owner ? Number(currency === "diamonds" ? owner.diamonds : owner.coins) : 0;
  const valid = decimalsOk && amt > 0 && (direction === "add" || amt <= current + 1e-9);

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await walletAdjust({ owner_type: type, owner_id: id, currency, direction, amount: String(amount).trim(), note: note || undefined });
      setOwner(r.owner);
      setMsg({ ok: true, text: `${direction === "add" ? "Added" : "Subtracted"} ${fmt(amt)} ${currency}.` });
      setAmount("");
      setNote("");
      setConfirming(false);
      setPage(1);
      await load();
    } catch (e) {
      setMsg({ ok: false, text: errorMessage(e, "Could not update the balance") });
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !owner) return <div className="rv-panel"><Loading /></div>;
  if (error) return <div className="rv-panel"><div className="rv-error">{error}</div></div>;
  if (!owner) return null;

  return (
    <div className="rv-panel">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div className="d-flex align-items-center gap-3">
          <Avatar img={owner.img} name={owner.name} size="lg" />
          <div>
            <div className="rv-title">{type === "user" ? `@${owner.name}` : owner.name}</div>
            <div className="rv-sub">{type === "user" ? `User #${owner.id} · ${owner.nick_name || ""}` : `Category #${owner.id}`}</div>
          </div>
        </div>
        <div className="d-flex gap-3">
          <div className="rv-kpi gold" style={{ minWidth: 150 }}>
            <div className="rv-kpi-label">Coins</div>
            <div className="rv-kpi-value">{fmt(owner.coins)}</div>
          </div>
          {type === "user" && (
            <div className="rv-kpi violet" style={{ minWidth: 150 }}>
              <div className="rv-kpi-label">Diamonds</div>
              <div className="rv-kpi-value">{fmt(owner.diamonds)}</div>
            </div>
          )}
        </div>
      </div>

      <div className="rv-panel mb-3" style={{ background: "var(--rv-panel-2)" }}>
        <div className="row g-3 align-items-end">
          {type === "user" && (
            <div className="col-md-auto">
              <label className="rv-label">Balance</label>
              <div className="rv-seg">
                {["coins", "diamonds"].map((c) => (
                  <button key={c} className={currency === c ? "active" : ""} onClick={() => setCurrency(c)}>
                    {c === "coins" ? "Coins" : "Diamonds"}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="col-md-auto">
            <label className="rv-label">Action</label>
            <div className="rv-seg violet">
              {[
                ["add", "Add"],
                ["subtract", "Subtract"],
              ].map(([k, l]) => (
                <button key={k} className={direction === k ? "active" : ""} onClick={() => setDirection(k)}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="col-md">
            <label className="rv-label">Amount</label>
            <input className="rv-input" type="number" min="0" step="any" inputMode="decimal" placeholder="e.g. 10 or 2.5" value={amount} onChange={(e) => setAmount(e.target.value)} />
            {amount && !decimalsOk && <div className="small mt-1" style={{ color: "#ff6b7d" }}>Up to 4 decimals.</div>}
          </div>
          <div className="col-12">
            <label className="rv-label">Note (shown in the history)</label>
            <input className="rv-input" maxLength={500} placeholder="e.g. Paid out 5,000 coins by bank transfer" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="col-12">
            {direction === "subtract" && amt > current && <div className="rv-error mb-2">Only {fmt(current)} {currency} available.</div>}
            {!confirming ? (
              <button className={`rv-btn ${direction === "add" ? "success" : "danger"}`} disabled={!valid} onClick={() => setConfirming(true)}>
                {direction === "add" ? "Add" : "Subtract"} {amt > 0 ? fmt(amt) : ""} {currency}
              </button>
            ) : (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span>
                  {direction === "add" ? "Add" : "Subtract"} <b>{fmt(amt)}</b> {currency} {direction === "add" ? "to" : "from"}{" "}
                  <b>{owner.name}</b>? New balance: <b>{fmt(direction === "add" ? current + amt : current - amt)}</b>
                </span>
                <button className="rv-btn violet" disabled={busy} onClick={submit}>
                  {busy ? "Saving…" : "Confirm"}
                </button>
                <button className="rv-btn ghost" disabled={busy} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            )}
            {msg && <div className={`mt-2 ${msg.ok ? "rv-success" : "rv-error"}`}>{msg.text}</div>}
          </div>
        </div>
      </div>

      <div className="rv-panel-title">History</div>
      {!txs.length ? (
        <div className="rv-empty">No movements yet.</div>
      ) : (
        <>
          <div className="rv-table-wrap">
            <table className="rv-table" style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>What</th>
                  <th className="rv-num">Amount</th>
                  <th className="rv-num">Balance after</th>
                  <th>By / note</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id}>
                    <td className="small text-grey" style={{ whiteSpace: "nowrap" }}>
                      {dateTime(t.created_at)}
                    </td>
                    <td>
                      {REASON_LABELS[t.reason] || t.reason}
                      <div className="small text-grey text-capitalize">{t.currency}</div>
                    </td>
                    <td className={`rv-num ${t.amount >= 0 ? "rv-amount-pos" : "rv-amount-neg"}`}>
                      {t.amount >= 0 ? "+" : ""}
                      {fmt(t.amount)}
                    </td>
                    <td className="rv-num">{t.balance_after !== null ? fmt(t.balance_after) : "—"}</td>
                    <td className="small">
                      {t.actor ? <span className="rv-badge rv-badge-violet me-1">{t.actor.name}</span> : null}
                      <span className="text-grey">{t.note || ""}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager meta={meta} onPage={setPage} />
        </>
      )}
    </div>
  );
}

function AllMovements() {
  const [ownerType, setOwnerType] = useState("");
  const [reason, setReason] = useState("");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => setPage(1), [ownerType, reason]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    walletTransactions({ owner_type: ownerType, reason, page, limit: 20 })
      .then((r) => alive && setRes(r))
      .catch(() => alive && setRes({ data: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [ownerType, reason, page]);

  return (
    <div className="rv-panel mt-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="rv-panel-title mb-0">All movements</div>
        <div className="d-flex gap-2">
          <select className="rv-select" value={ownerType} onChange={(e) => setOwnerType(e.target.value)}>
            <option value="">Users + categories</option>
            <option value="user">Users</option>
            <option value="category">Categories</option>
          </select>
          <select className="rv-select" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Every reason</option>
            {Object.entries(REASON_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading && !res ? (
        <Loading />
      ) : !res?.data?.length ? (
        <div className="rv-empty">No movements.</div>
      ) : (
        <>
          <div className="rv-table-wrap">
            <table className="rv-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Owner</th>
                  <th>What</th>
                  <th className="rv-num">Amount</th>
                  <th className="rv-num">Balance after</th>
                  <th>By / note</th>
                </tr>
              </thead>
              <tbody>
                {res.data.map((t) => (
                  <tr key={t.id}>
                    <td className="small text-grey" style={{ whiteSpace: "nowrap" }}>
                      {dateTime(t.created_at)}
                    </td>
                    <td>
                      <UserCell user={t.owner} sub={t.owner?.type === "category" ? "Category" : t.owner?.nick_name} />
                    </td>
                    <td>
                      {REASON_LABELS[t.reason] || t.reason}
                      <div className="small text-grey text-capitalize">{t.currency}</div>
                    </td>
                    <td className={`rv-num ${t.amount >= 0 ? "rv-amount-pos" : "rv-amount-neg"}`}>
                      {t.amount >= 0 ? "+" : ""}
                      {fmt(t.amount)}
                    </td>
                    <td className="rv-num">{t.balance_after !== null ? fmt(t.balance_after) : "—"}</td>
                    <td className="small">
                      {t.actor ? <span className="rv-badge rv-badge-violet me-1">{t.actor.name}</span> : null}
                      <span className="text-grey">{t.note || ""}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager meta={res.meta} onPage={setPage} />
        </>
      )}
    </div>
  );
}
